import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Switch, Row, Col, Space, Typography, Alert } from 'antd';
import { getCategoryDisplayName } from '../../../hooks/useUnits';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

/**
 * UnitsForm Component
 * ฟอร์มสำหรับเพิ่ม/แก้ไข/ดูข้อมูลหน่วยนับ
 */
const UnitsForm = ({
  visible,
  loading,
  editingUnit,
  viewOnly = false,
  categories,
  onSave,
  onCancel
}) => {
  const [form] = Form.useForm();

  // หมวดหมู่หน่วยนับที่รองรับ
  const unitCategories = [
    { value: 'count', label: 'หน่วยนับ', description: 'การนับจำนวน เช่น ชิ้น, อัน, คู่' },
    { value: 'weight', label: 'น้ำหนัก', description: 'การชั่งน้ำหนัก เช่น กิโลกรัม, กรัม, ตัน' },
    { value: 'volume', label: 'ปริมาตร', description: 'การวัดปริมาตร เช่น ลิตร, มิลลิลิตร, แกลลอน' },
    { value: 'length', label: 'ความยาว', description: 'การวัดความยาว เช่น เมตร, เซนติเมตร, นิ้ว' },
    { value: 'area', label: 'พื้นที่', description: 'การวัดพื้นที่ เช่น ตารางเมตร, ไร่, งาน' },
    { value: 'time', label: 'เวลา', description: 'การวัดเวลา เช่น ชั่วโมง, นาที, วัน' },
    { value: 'container', label: 'ภาชนะ', description: 'การใส่ภาชนะ เช่น ลัง, ถุง, ขวด' }
  ];

  /**
   * เมื่อ Modal เปิด/ปิด
   */
  useEffect(() => {
    if (visible && editingUnit) {
      // แก้ไข - ตั้งค่าข้อมูลเดิม
      form.setFieldsValue({
        unit_name: editingUnit.unit_name,
        unit_code: editingUnit.unit_code || '',
        category: editingUnit.category,
        display_order: editingUnit.display_order,
        is_active: editingUnit.is_active,
        description: editingUnit.description || ''
      });
    } else if (visible && !editingUnit) {
      // เพิ่มใหม่ - รีเซ็ตฟอร์ม
      form.resetFields();
      // ตั้งค่าเริ่มต้น
      form.setFieldsValue({
        is_active: true,
        display_order: getNextDisplayOrder()
      });
    }
  }, [visible, editingUnit, form]);

  /**
   * คำนวณ display_order ถัดไป
   */
  const getNextDisplayOrder = () => {
    if (!categories || categories.length === 0) return 1;
    
    // หาค่าสูงสุดของ display_order ในหมวดหมู่ที่เลือก
    const selectedCategory = form.getFieldValue('category');
    if (!selectedCategory) return 1;
    
    // ในกรณีนี้เราใช้ค่าเริ่มต้น เพราะไม่มีข้อมูล units ตาม category
    return 1;
  };

  /**
   * Validation rules
   */
  const validationRules = {
    unit_name: [
      { required: true, message: 'กรุณากรอกชื่อหน่วยนับ' },
      { max: 50, message: 'ชื่อหน่วยนับต้องไม่เกิน 50 ตัวอักษร' },
      { min: 1, message: 'ชื่อหน่วยนับต้องมีอย่างน้อย 1 ตัวอักษร' },
      {
        validator: async (_, value) => {
          if (!value || !value.trim()) return;
          
          // ตรวจสอบชื่อซ้ำ (เฉพาะกรณีเพิ่มใหม่หรือแก้ไขชื่อ)
          if (!editingUnit || editingUnit.unit_name !== value.trim()) {
            // TODO: เพิ่มการตรวจสอบชื่อซ้ำกับ API
            // const isDuplicate = await checkDuplicateName(value.trim());
            // if (isDuplicate) throw new Error('ชื่อหน่วยนับนี้มีอยู่แล้ว');
          }
        }
      }
    ],
    unit_code: [
      { max: 10, message: 'รหัสหน่วยต้องไม่เกิน 10 ตัวอักษร' },
      { 
        pattern: /^[a-zA-Z0-9]*$/, 
        message: 'รหัสหน่วยใช้ได้เฉพาะตัวอักษรภาษาอังกฤษและตัวเลข' 
      },
      {
        validator: async (_, value) => {
          if (!value || !value.trim()) return;
          
          // ตรวจสอบรหัสซ้ำ (เฉพาะกรณีเพิ่มใหม่หรือแก้ไขรหัส)
          if (!editingUnit || editingUnit.unit_code !== value.trim()) {
            // TODO: เพิ่มการตรวจสอบรหัสซ้ำกับ API
            // const isDuplicate = await checkDuplicateCode(value.trim());
            // if (isDuplicate) throw new Error('รหัสหน่วยนี้มีอยู่แล้ว');
          }
        }
      }
    ],
    category: [
      { required: true, message: 'กรุณาเลือกหมวดหมู่' }
    ],
    display_order: [
      { required: true, message: 'กรุณากรอกลำดับการแสดง' },
      { type: 'number', min: 1, max: 999, message: 'ลำดับการแสดงต้องอยู่ระหว่าง 1-999' }
    ],
    description: [
      { max: 255, message: 'คำอธิบายต้องไม่เกิน 255 ตัวอักษร' }
    ]
  };

  /**
   * บันทึกข้อมูล
   */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // ทำความสะอาดข้อมูล
      const cleanedValues = {
        unit_name: values.unit_name.trim(),
        unit_code: values.unit_code ? values.unit_code.trim() : null,
        category: values.category,
        display_order: values.display_order,
        is_active: values.is_active,
        description: values.description ? values.description.trim() : null
      };

      onSave(cleanedValues);
    } catch (error) {
      // Form validation errors จะแสดงใน form แล้ว
    }
  };

  /**
   * ยกเลิกและปิด Modal
   */
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  /**
   * จัดการเมื่อหมวดหมู่เปลี่ยน
   */
  const handleCategoryChange = (category) => {
    // อัปเดต display_order ตามหมวดหมู่ใหม่
    if (!editingUnit) {
      form.setFieldsValue({
        display_order: getNextDisplayOrder()
      });
    }
  };

  const title = viewOnly ? 'ดูรายละเอียดหน่วยนับ' : 
               editingUnit ? 'แก้ไขหน่วยนับ' : 'เพิ่มหน่วยนับใหม่';

  return (
    <Modal
      title={title}
      open={visible}
      onOk={viewOnly ? onCancel : handleSave}
      onCancel={handleCancel}
      width={600}
      confirmLoading={loading}
      okText={viewOnly ? 'ปิด' : editingUnit ? 'บันทึก' : 'เพิ่ม'}
      cancelText="ยกเลิก"
      maskClosable={false}
      destroyOnClose
    >
      {/* คำเตือนสำหรับการแก้ไข */}
      {editingUnit && !viewOnly && (
        <Alert
          message="การแก้ไขหน่วยนับ"
          description="การเปลี่ยนแปลงหน่วยนับอาจส่งผลต่อข้อมูลสินค้าที่เกี่ยวข้อง กรุณาตรวจสอบให้แน่ใจก่อนบันทึก"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          is_active: true,
          display_order: 1
        }}
        disabled={viewOnly}
      >
        <Row gutter={16}>
          {/* ชื่อหน่วยนับ */}
          <Col span={16}>
            <Form.Item
              label="ชื่อหน่วยนับ"
              name="unit_name"
              rules={validationRules.unit_name}
              extra="ชื่อหน่วยนับที่จะแสดงในระบบ เช่น กิโลกรัม, ชิ้น, ลิตร"
            >
              <Input 
                placeholder="กรอกชื่อหน่วยนับ"
                maxLength={50}
                showCount
              />
            </Form.Item>
          </Col>

          {/* รหัสหน่วย */}
          <Col span={8}>
            <Form.Item
              label="รหัสหน่วย"
              name="unit_code"
              rules={validationRules.unit_code}
              extra="รหัสสั้นๆ เช่น kg, pcs, l (ไม่บังคับ)"
            >
              <Input 
                placeholder="เช่น kg, pcs"
                maxLength={10}
                showCount
                style={{ textTransform: 'lowercase' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* หมวดหมู่ */}
          <Col span={16}>
            <Form.Item
              label="หมวดหมู่"
              name="category"
              rules={validationRules.category}
            >
              <Select
                placeholder="เลือกหมวดหมู่หน่วยนับ"
                onChange={handleCategoryChange}
                showSearch
                optionFilterProp="children"
              >
                {unitCategories.map(cat => (
                  <Option key={cat.value} value={cat.value}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{cat.label}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {cat.description}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ลำดับแสดง */}
          <Col span={8}>
            <Form.Item
              label="ลำดับแสดง"
              name="display_order"
              rules={validationRules.display_order}
              extra="ลำดับที่แสดงในรายการ (1-999)"
            >
              <InputNumber
                min={1}
                max={999}
                style={{ width: '100%' }}
                placeholder="1"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* คำอธิบาย */}
        <Form.Item
          label="คำอธิบาย"
          name="description"
          rules={validationRules.description}
        >
          <TextArea
            placeholder="คำอธิบายเพิ่มเติมเกี่ยวกับหน่วยนับนี้ (ไม่บังคับ)"
            rows={3}
            maxLength={255}
            showCount
          />
        </Form.Item>

        {/* สถานะการใช้งาน */}
        <Form.Item
          label="สถานะการใช้งาน"
          name="is_active"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="ใช้งาน"
            unCheckedChildren="ไม่ใช้งาน"
          />
        </Form.Item>

        {/* แสดงข้อมูลเพิ่มเติมเมื่อแก้ไข */}
        {editingUnit && (
          <div style={{ 
            padding: 12, 
            background: '#fafafa', 
            borderRadius: 6,
            border: '1px solid #f0f0f0'
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>ข้อมูลระบบ:</strong>
            </Text>
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              <div>ID: {editingUnit.id}</div>
              <div>สร้างเมื่อ: {new Date(editingUnit.created_at).toLocaleString('th-TH')}</div>
              {editingUnit.updated_at && (
                <div>แก้ไขล่าสุด: {new Date(editingUnit.updated_at).toLocaleString('th-TH')}</div>
              )}
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default UnitsForm;